import { z } from 'zod';
import { DrawerHeader } from '../../drawer/DrawerHeader';
import { DrawerWrapper } from '../../drawer/DrawerWrapper';
import { FC, Fragment } from 'react';
import { Skeleton, Stack, Typography } from '@mui/material';
import { useApiQuery } from '../../query/hooks';
import { graphql } from '../../gql';
import { FormattedMessage, useIntl } from 'react-intl';
import { PersonaAvatarSmall } from '../../persona/PersonaAvatar';
import { flattenDeep } from 'lodash';
import { SummaryList } from './SummaryList';
import { SummarySatisfactionBlock } from './SummarySatisfactionBlock';
import { MetricRow } from './SummaryMetricRow';

const queryJourneySummary = graphql(`
	query journey_summaryDrawer($id: UUID!) {
		getJourney(by: { id: $id }) {
			name
			satisfaction
			phases {
				touchpoints {
					activity {
						id
					}
				}
			}
			contentMetrics {
				id
				activities {
					name
				}
				metric {
					id
					unitSign
					dataSet {
						metricTemplate {
							unitSign
						}
					}
				}
			}
			personas {
				id
				name
				activities {
					id
				}
				...PersonaAvatar_persona
			}
		}
	}
`);

export const summaryDrawerSchema = z.object({
	journeyId: z.string(),
});

export const SummaryDrawer: FC<z.infer<typeof summaryDrawerSchema>> = ({ journeyId }) => {
	const intl = useIntl();
	const { data, isLoading } = useApiQuery(queryJourneySummary, { variables: { id: journeyId } });
	const amountOfActivities = flattenDeep(data?.getJourney?.phases.map((phase) => phase.touchpoints) ?? []).length;

	if (isLoading) {
		return (
			<DrawerWrapper>
				<DrawerHeader title={intl.formatMessage({ id: 'journey.summary.drawer.title' })} />
				<Stack mt={2} gap={2} width={'100%'}>
					<Skeleton height={100} />
					<Stack>
						<Skeleton height={50} />
						<Skeleton height={50} />
						<Skeleton height={50} />
						<Skeleton height={50} />
					</Stack>
				</Stack>
			</DrawerWrapper>
		);
	}

	return (
		<DrawerWrapper>
			<DrawerHeader title={intl.formatMessage({ id: 'journey.summary.drawer.title' })} />
			<Stack mt={2} gap={2} width={'100%'}>
				{/* Satisfaction meter */}
				<SummarySatisfactionBlock satisfaction={data?.getJourney?.satisfaction ?? 0} name={data?.getJourney?.name ?? ''} />
				{/* Metrics List */}
				<SummaryList
					listItems={(data?.getJourney?.contentMetrics ?? []).map((contentMetric) => (
						<MetricRow
							contentMetric={contentMetric}
							unitSign={contentMetric.metric?.unitSign ?? contentMetric.metric?.dataSet?.metricTemplate?.unitSign ?? null}
							key={contentMetric.id}
						/>
					))}
				/>
				{/* Personas List */}
				<SummaryList
					title={<FormattedMessage id={'journey.summary.drawer.personas.title'} />}
					header={
						<>
							<Typography fontWeight={500} sx={{ flexBasis: '50%' }}>
								<FormattedMessage id={'journey.summary.drawer.personas.name'} />
							</Typography>
							<Typography fontWeight={500} sx={{ flexBasis: '25%', textAlign: 'right' }}>
								<FormattedMessage id={'journey.summary.drawer.personas.reach'} />
							</Typography>
						</>
					}
					showHeader
					listItems={(data?.getJourney?.personas ?? []).map((persona) => (
						<Fragment key={persona.id}>
							<Stack direction={'row'} alignItems={'center'} gap={1} flexBasis={'50%'}>
								<PersonaAvatarSmall persona={persona} />
								<Typography fontWeight={500} fontSize={14}>
									{persona.name}
								</Typography>
							</Stack>
							<Typography textAlign={'right'} fontSize={14}>
								{/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
								{Math.ceil((persona.activities.length / amountOfActivities) * 100)}%
							</Typography>
						</Fragment>
					))}
				/>
			</Stack>
		</DrawerWrapper>
	);
};
